import { ContentBrick } from '../../../../../app-kit/components/ContentBrick/ContentBrick';
import { useShowAVMLoggedOut } from '../../../../hooks/useShowAVMLoggedOut';
import {
  Inline,
  Link,
  Text,
  mediaHelper,
  spacingHelper,
  typographyHelper,
  paletteHelper,
  Badge,
  radiusHelper,
} from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import type { Currency, ValueEstimateConfidence, ValueEstimateRange } from './types';
import { ValuationType } from './types';
import { useJoinUsUrl } from '../../../../../hooks/useJoinUsUrl';
import { useUrlContext } from '../../../../../contexts/UrlContext/UrlContext';

export interface PropertyValuationSubBrickProps {
  title: string;
  subtitle?: string | null;
  confidence: ValueEstimateConfidence;
  price?: Currency;
  priceSubtitle?: string | null;
  valuationType: ValuationType;
  range?: ValueEstimateRange;
  emptyEstimateMessage?: string | null;
  hiddenEstimateMessage?: string | null;
  hideConfidenceLabel: boolean;
}

const nonBreakingSpace = '\u00A0';

const PropertyValuationSubBrickContainer = styled(ContentBrick)`
  margin: unset;

  ${mediaHelper('desktopMin')} {
    flex-grow: 1;
    width: 0;
  }
`;

const TitleText = styled(Text)`
  ${typographyHelper('subtitle01')};
  font-size: 1rem;
`;

const ConfidenceText = styled(Text)<{ $valuationType: ValuationType }>`
  color: ${({ $valuationType }) =>
    $valuationType === ValuationType.sale ? paletteHelper('textBrand') : paletteHelper('textBlue')};
  text-transform: uppercase;
  letter-spacing: 0.07rem;
  ${typographyHelper('overline')};
  margin-bottom: ${spacingHelper('twoExtraSmall')};
`;

const PriceText = styled(Text)`
  ${typographyHelper('title02')};
`;

const PriceEstimate = styled.div<{ $valuationType: ValuationType; $showAVMData: boolean }>`
  position: relative;
  background-color: ${({ $valuationType, $showAVMData }) => {
    if (!$showAVMData) return 'transparent';

    return $valuationType === ValuationType.sale
      ? paletteHelper('backgroundPurpleSecondary')
      : paletteHelper('backgroundBlueSecondary');
  }};

  border-radius: ${radiusHelper('medium')};
  padding-top: ${({ $showAVMData }) => ($showAVMData ? spacingHelper('medium') : '0')};
  padding-bottom: ${spacingHelper('medium')};
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${spacingHelper('small')};
`;

const EstimateRange = styled(Inline)`
  justify-content: space-between;
`;

const MinPrice = styled(Text)`
  ${typographyHelper('subtitle02')};
  font-weight: 600;
  font-size: 0.9rem;
`;
const MaxPrice = styled(MinPrice)`
  text-align: right;
`;
const MinLabel = styled(Text)`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
`;

const MaxLabel = styled(MinLabel)`
  text-align: right;
`;

const EmptyEstimateMessage = styled(Text)`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
  padding-top: ${spacingHelper('small')};
`;

const HiddenEstimateMessage = styled.div`
  width: 100%;
`;

const PricePerUnitBadge = styled(Badge)`
  position: absolute;
  bottom: 0;
  transform: translateY(50%);
  background-color: ${paletteHelper('backgroundPurple')};
  color: ${paletteHelper('textPrimary')};
  box-shadow: none;
`;

export const PropertyValuationSubBrick: FunctionComponent<PropertyValuationSubBrickProps> = ({
  title,
  subtitle,
  confidence,
  price,
  priceSubtitle,
  valuationType,
  range,
  emptyEstimateMessage,
  hiddenEstimateMessage,
  hideConfidenceLabel,
}) => {
  const showAVMData = useShowAVMLoggedOut();
  const url = useUrlContext();
  const joinUsUrl = useJoinUsUrl(url.pathname);

  return (
    <PropertyValuationSubBrickContainer
      titleComponent={<TitleText as="h3">{title}</TitleText>}
      subtitle={subtitle ?? nonBreakingSpace}
      isSubBrick={true}
    >
      <PriceEstimate $valuationType={valuationType} $showAVMData={showAVMData}>
        {showAVMData ? (
          <>
            {!hideConfidenceLabel && (
              <ConfidenceText
                data-testid="valuation-sub-brick-confidence"
                $valuationType={valuationType}
              >
                {confidence.label}
              </ConfidenceText>
            )}
            <PriceText data-testid="valuation-sub-brick-price-text">
              {price?.valueWithUnit && price.valueWithUnit}
            </PriceText>
            {valuationType === ValuationType.sale && priceSubtitle && (
              <PricePerUnitBadge>{priceSubtitle}</PricePerUnitBadge>
            )}
          </>
        ) : (
          <HiddenEstimateMessage>
            <Link href={joinUsUrl} variant="button-outline" sizeVariant="medium" fullWidth>
              {hiddenEstimateMessage}
            </Link>
          </HiddenEstimateMessage>
        )}
      </PriceEstimate>
      {emptyEstimateMessage ? (
        <EmptyEstimateMessage>{emptyEstimateMessage}</EmptyEstimateMessage>
      ) : (
        showAVMData &&
        range && (
          <>
            <EstimateRange data-testid="valuation-sub-brick-estimate-range">
              <MinPrice>{range.min.valueWithUnit}</MinPrice>
              <MaxPrice>{range.max.valueWithUnit}</MaxPrice>
            </EstimateRange>
            <EstimateRange>
              <MinLabel>{range.minTitle}</MinLabel>
              <MaxLabel>{range.maxTitle}</MaxLabel>
            </EstimateRange>
          </>
        )
      )}
    </PropertyValuationSubBrickContainer>
  );
};
